// @ts-nocheck - TODO: remove
/* eslint-disable no-debugger,no-undef,no-unreachable,@typescript-eslint/no-unused-vars */

import {
	Account,
	getWebsiteBehaviors,
	Page,
	Project,
	Selection,
	Utils
} from "../../main/index.js";
import AsyncLock from "async-lock";
import CommentMenu, {COMMENT_MENU_JS_SELECTOR} from "./comment-menu.js";
import ProjectTopicMenu, {getProjectTopicMenu, PROJECT_TOPIC_MENU_JS_SELECTOR} from "./project-topic-menu.js";
import UrlUtils from "./common/url-utils.js";
import {copiedConfirmationMsg} from "./common/components/CopyButton.jsx";
import {setStickyTooltips} from "./common/tippy-control.js";
import Data from "../../main/data/index.js";
import {action, computed, makeObservable, observable, reaction, runInAction, when} from "mobx";
import WebsiteBehaviors from "../../main/website-behaviors/index.js";
import type {
	annotation, annotationStub, annotationUuid,
	annotationCitation, annotationSelection,
	projectUuid,
	topic, topicUuid
} from "../../main/data/model/index.js";
import {isEqual} from "lodash";
import Async from "../../main/utils/async.js";
import Citation from "./citation.js";
import Integration from "../integration/index.js";
import Session from "../session/index.js";
import {toast} from "../../lit-components/common/toast/toastControl.js";
import hotkeys from "hotkeys-js";
import Renderer, {pageCoords} from "../../main/render.js";
import {getHighlightToggle} from "../../main/utils/highlight.js";

const PN_STYLESHEET = "content-script.css";
const PN_WEB_COMPONENTS_PATH = "js/WebComponents.js";

const INIT_STATE_UNINITIALIZED = "uninitialized";
const INIT_STATE_INIT_IN_PROGRESS = "init-in-progress";
const INIT_STATE_INIT_COMPLETE = "init-complete";

// Alert users about highlighting with a shortcut every few minutes
const KEYBOARD_SELECTION_TOAST_INTERVAL = 5 * 60 * 1000;

/* global PDFViewerApplication */

/**
 * App object, it handles client and UI logic.
 */
class Highlighter {
	// state
	isHighlightingEnabled      = false;
	isHighlightEditingEnabled  = false;
	active                     = false;
	integrationPreDataMode     = false;

	// Any highlight that fails to render is added here with an exp.backoff retry function
	highlightRetries: Record<annotationUuid, () => Promise<void>> = {};

	// locks
	lock                       = new AsyncLock({timeout: 10000, maxPending: 3});

	// Local cache
	ctx                        = {}; // E.g. {tabId: 12, frameId: 34}
	initState                  = INIT_STATE_UNINITIALIZED;
	isProjectsSwitchFromMenu   = false;

	// Highlighting state
	bypassHighlightRules       = false;
	lastMouseCoords:  pageCoords | null            = null;
	lastSelectionPre: annotationSelection | null           = null;
	lastEvent:        MouseEvent | null                  = null;
	// TODO: possibly temporary, before switching to temporary highlights
	lastSelection:    annotationSelection | null              = null;
	lastSelectedText          = "";

	// Keyboard selection
	isSelectingKeyboard = false; // Is the user making a selection with keyboard
	isKeyboardSelection = false; // Is the current selection made with keyboard
	lastKeyboardHighlightToast = null; // Time of last toast shown to user

	// Keyboard location
	lastFocusedNode: Element | null            = null;

	// Image selection
	lastClickedTarget: Element | null          = null;

	// Z-index
	zIndex                     = 2147483627;

	// Page utils
	page = new Page();

	// Behaviors for this page (includes customizations for special websites)
	websiteBehaviors: WebsiteBehaviors | null = null;

	// Renderer
	renderer: Renderer = new Renderer(); // initialized properly (including hooks) when everything else inits

	private projectTopicMenu: ProjectTopicMenu;
	private commentMenu:      CommentMenu;

	constructor() {
		makeObservable(this, {
			isHighlightingEnabled:     observable,
			isHighlightEditingEnabled: observable,
			active:                    observable,
			integrationPreDataMode:    observable,

			enableHighlighting:  action,
			disableHighlighting: action,
			destroy:             action,

			hasEditAccess:                  computed,
			supportLevel:                   computed,
			timeTrackingTermsPendingReview: computed
		});

		// TODO: disposer?
		reaction(() => ({
			enabled:                        this.isHighlightingEnabled,
			supportLevel:                   this.supportLevel,
			timeTrackingTermsPendingReview: this.timeTrackingTermsPendingReview,
			pageHighlights:                 Data.app.pageHighlights
		}), (
			{
				enabled,
				supportLevel,
				timeTrackingTermsPendingReview,
				pageHighlights
			},
			{
				pageHighlights: prevPageHighlights
			}
		) => {
			if (
				!enabled
				|| !this.validateFeatures(supportLevel)
				|| timeTrackingTermsPendingReview
			) {
				this.renderer.removeAllAnnotations();
				return;
			}

			const uuids = Object.keys(pageHighlights) as annotationUuid[];
			const prevUuids = Object.keys(prevPageHighlights) as annotationUuid[];

			const newUuids = uuids.filter(uuid => !prevUuids.includes(uuid));
			const deletedUuids = prevUuids.filter(uuid => !uuids.includes(uuid));
			const keptUuids = uuids.filter(uuid => prevUuids.includes(uuid));

			newUuids.forEach(uuid => {
				const {annotation, topic} = pageHighlights[uuid];
				// noinspection JSIgnoredPromiseFromCall
				this.renderHighlight(annotation, topic);
			});

			deletedUuids.forEach(this.removeHighlight);

			keptUuids.forEach(uuid => {
				const {annotation, topic} = pageHighlights[uuid];
				if (
					!isEqual(annotation, prevPageHighlights[uuid].annotation)
					|| !isEqual(topic, prevPageHighlights[uuid].topic)
				) {
					this.removeHighlight(uuid);
					// noinspection JSIgnoredPromiseFromCall
					this.renderHighlight(annotation, topic);
				}
			});
		}, {
			delay: 50 // Some delay to allow the page to render (reduces the probability of failures).
		});

		// React to user preferences (e.g. enabling high contrast)
		reaction(() => Data.app.me, (current, previous) => {
			if (!current || !previous)
				return;

			const prefsBefore = Account.getPrefsFromAccount(current);
			const prefsAfter = Account.getPrefsFromAccount(previous);

			const renderPreferencesChanged = (
				prefsBefore.highContrastMode !== prefsAfter.highContrastMode
				|| prefsBefore.extension_highlightColor !== prefsAfter.extension_highlightColor
			);
			if (renderPreferencesChanged)
				this.renderAnnotationsOnPage();

			const stickyTooltipsPrefsChanged = prefsBefore.stickyTooltips !== prefsAfter.stickyTooltips;
			if (stickyTooltipsPrefsChanged)
				setStickyTooltips(prefsAfter.stickyTooltips);
		});

		this.initKeyboardHighlights();
	}

	/**
	 * Render a highlight on the page.
	 * Retry a few times if it fails.
	 */
	renderHighlight = async (annotation: annotation, topic: topic) => {
		const uuid = annotation.uuid;
		const success = this.renderer.renderHighlight(
			annotation,
			this.websiteBehaviors,
			Data.app.me,
			topic.color
		);

		success ? Data.app.unmarkHighlightAsUnrenderable(uuid) : Data.app.markHighlightAsUnrenderable(uuid);

		if (success) {
			if (this.highlightRetries[uuid] !== undefined) {
				this.highlightRetries[uuid].cancel();
				delete this.highlightRetries[uuid];
			}
		} else {
			if (this.highlightRetries[uuid] === undefined) {
				this.highlightRetries[uuid] = Utils.Async.exponentialBackoff(this.renderHighlight, {
					retries:           5,
					minTimeout:        1000,
					maxTimeout:        5000,
					exponentialFactor: 1.5
				});
			}

			// Retry using exponential backoff logic
			setTimeout(async () => {
				try {
					await this.highlightRetries[uuid](annotation, topic);
				} catch (e) {
					if (this.highlightRetries[uuid] !== undefined)
						delete this.highlightRetries[uuid];
				}
			}, 0);
		}
	};

	removeHighlight = (uuid: annotationUuid) => {
		this.renderer.removeHighlight(uuid);
		Data.app.unmarkHighlightAsUnrenderable(uuid);

		if (this.highlightRetries[uuid] !== undefined) {
			this.highlightRetries[uuid].cancel();
			delete this.highlightRetries[uuid];
		}
	};

	handleChangeHash = () => {
		const hash = window.location.hash;
		const isUsingRouting = Utils.Document.isLocationHashUsingRouting(hash);

		if (isUsingRouting)
			this.renderAnnotationsOnPage();
	};

	/**
	 * @deprecated
	 */
	selectProject = async (projectUuid, successCb) => {
		debugger;
		const before = this.store.selectedProject;

		if (before !== projectUuid)
			await this.changeProjectTo(projectUuid);

		if (typeof successCb === "function")
			successCb();
	};

	/**
	 * Handles change of selected project.
	 *
	 * @deprecated TODO: implement mobx reaction to project changing
	 */
	changeProjectTo = async (projectUuid) => {
		debugger;
		// default initiatedFromSidebar is undefined/false
		// by default it will inform the sidebar
		await this.lock.acquire("changeProject", async () => {
			const previous = this.store.selectedProject;
			try {
				await this.store.switchProject(projectUuid);
			} catch (errorType) {
				return;
			}

			const prevHighlightingEnabled = this.isHighlightingEnabled;
			this.enableHighlighting(); // disables highlighting in certain cases (e.g. time-tracking terms pending review)
			const highlightingEnabledChanged = this.isHighlightingEnabled !== prevHighlightingEnabled;

			if (previous === projectUuid && !highlightingEnabledChanged)
				return;

			// Remove any popup that might have been opened before

			// If topic menu is switching projects, don't do anything to it,
			// the menu will be updated in project-topic-menu.js
			if (this.projectTopicMenu.isMenuOpen()) {
				const selectedProjectBtn = getProjectTopicMenu().querySelector(".project-item.selected button");
				const menuProjectUuid = selectedProjectBtn.getAttribute("data-uuid");

				if (menuProjectUuid === projectUuid)
					this.projectTopicMenu.destroy();
				else {
					// Set flag to not rerender menu until switch is complete
					this.isProjectsSwitchFromMenu = true;
				}
			}

			this.commentMenu.destroy();

			// Remove any selection that might cause an issue when changing projects
			Selection.ClearTextSelection();
			this.renderer.removeAllAnnotations();

			if (projectUuid == null) {
				this.disableHighlightEditing();
				return;
			}

			// Respect project permissions
			this.invokeHighlightingRights();

			this.renderAnnotationsOnPage();

			// Persist selected project for current tab
			await PnTabManager.setProjectForTab(this.ctx.tabId, this.store.selectedProject);

			// Start time tracking when appropriate
			// this.updateTimeTracking();
		}); // end lock
	};

	/**
	 * Check the access rights the user has for the active project,
	 * and either enable or disable highlight editing if necessary.
	 */
	invokeHighlightingRights = () => {
		if (this.isHighlightEditingEnabled && !this.hasEditAccess)
			this.disableHighlightEditing();
		else if (!this.isHighlightEditingEnabled && this.hasEditAccess)
			this.enableHighlightEditing();
	};

	/**
	 * Copy the selected text to the user's clipboard.
	 * This is called from the topic menu, which provides the user a copy button
	 * when it appears during highlight creation.
	 */
	copySelectedText = async () => {
		this.renderer.removeTmpAnnotation.bind(this.renderer)();
		this.renderer.recoverLostSelection.bind(this.renderer)();

		// Copy from highlighter menu.
		// Get the citation if we know how to, otherwise just copy the selected text.
		const selectedText = Selection.GetSelectedText();

		const preferredCitationStyle = Project.getCitationStyle(Data.app.project);

		const result = await this.websiteBehaviors.copySelectionToClipboard(selectedText, document, preferredCitationStyle);

		if (result !== false)
			this.topFrameShowMessageOnSidebarButton(copiedConfirmationMsg);
	};

	topFrameShowMessageOnSidebarButton = (msg, fromAnotherFrame = false) => {
		/* if (this.sidebarWrapper)
			this.sidebarWrapper.showMessageOnSidebarButton(msg);
		else*/ if (fromAnotherFrame)
			return;
		// else
		// 	this.sendMessageToTopFrame("topFrameShowMessageOnSidebarButton", {msg});
	};

	/**
	 * Handles click on any highlight.
	 */
	onHighlightSelect = (x: number, y: number, uuid: annotationUuid) => {
		const annotation = Data.annotation.byUuid(uuid);

		if (annotation) {
			const offset = this.calculatePositioningOffset();
			this.commentMenu.renderCommentsMenu(y + offset.y, x + offset.x, annotation, false, this.hasEditAccess);
		}
	};

	/**
	 * Uses the currently selected text to create a highlight annotation in the specified topic.
	 */
	generateHighlightStub = (): annotationStub => {
		const currentSelection = Selection.GetCurrentSelection();

		const data = {
			version:      "2",
			type:         "highlight",
			pageData:     this.page.getPageData.bind(this.page)(),
			selection:    currentSelection ? currentSelection : this.lastSelection,
			selectedText: currentSelection ? Selection.GetSelectedText() : this.lastSelectedText
		};

		if (this.websiteBehaviors)
			this.websiteBehaviors.alterNewHighlight(data);

		return data as annotationStub;
	};

	generateImageStub = (extra: {selection?: annotationSelection; citation?: annotationCitation} = {}): annotationStub => {
		const data = {
			version:     "2",
			type:        "image",
			file_status: "pending",
			pageData:    this.page.getPageData.bind(this.page)(),
			...extra
		};

		return data as annotationStub;
	};

	/**
	 * Remove all annotations from UI.
	 */
	removeAllAnnotations = () => {
		this.renderer.removeAllAnnotations.bind(this.renderer)();
	};

	/**
	 * Returns true if the current page (domain) is forbidden to have
	 * the extension enabled/active.
	 *
	 * The account object can optionally be passed for cases where this
	 * is used prior to the store being initialized.
	 * (see runIfEnabled() inside this.init())
	 */
	isPageForbidden = (): boolean => {
		const account = Data.app.me;
		const userBlacklist = Account.getHostBlacklistFromAccount(account);
		return UrlUtils.isBlacklisted(window.location.toString(), userBlacklist);
	};

	/**
	 * Apply a new comment to the existing annotation (based on a local change).
	 * This is used from the comment popup (handleSave())
	 * and the sidebar (onUpdateNote() function passed to outline panel).
	 */
	updateAnnotationComment = async (annotationUuid: annotationUuid, comment: string, onSuccessCallback?: () => void) => {
		if (!Data.app.project)
			throw new Error();
		await Data.annotation.update(annotationUuid, Data.app.project.uuid, {comment});
		if (onSuccessCallback)
			onSuccessCallback();
		// updateAnnotationComment(this, annotationUuid, comment, onSuccessCallback);
	};

	/**
	 * Update the topic for the specified annotation.
	 */
	updateAnnotationTopic = (annotationUuid: annotationUuid, topicUuid: topicUuid) => {
		if (!Data.app.project)
			throw new Error();
		Data.annotation.moveToTopic(annotationUuid, Data.app.project.uuid, topicUuid);
	};

	/**
	 * It does the following things:
	 * - Changes project and loads its annotations.
	 * - Scrolls to given annotation on both the page and sidebar.
	 */
	loadProjectAndScrollToAnnotation = (projectUuid: projectUuid, annotationUuid: annotationUuid) => {
		debugger; // TODO: probably use Data.app.setCurrentProject and then Async.waitUntil(() => Data.annotation.byUuid(annotationUuid))
		this.selectProject(projectUuid, () => {
			this.renderer.scrollToPageAnnotation.bind(this.renderer)(annotationUuid);
		});
	};

	/**
	 * This appears to be called from a number of places,
	 * perhaps asynchronously, so be careful.
	 */
	renderAnnotationsOnPage = () => {
		if (!this.isHighlightingEnabled) {
			Data.app.clearCurrentPage();
			return;
		}

		const url = this.page.getPageUrl.bind(this.page)();
		if (url === Data.app.pageUrl) {
			// Not the most elegant, but will do for now (this forces re-rendering all annotations)
			Data.app.clearCurrentPage();
			setTimeout(() => Data.app.setCurrentPage(url), 50);
		} else
			Data.app.setCurrentPage(url);
	};

	handleOnContextMenu = (evt: MouseEvent) => {
		this.projectTopicMenu.destroy();

		this.lastMouseCoords = this.getPageCoordsFromEvt(evt);
		this.lastEvent = evt;
		// TODO: possibly temporary, before switching to temporary highlights
		this.lastSelection = Selection.GetCurrentSelection();
		this.lastSelectedText = Selection.GetSelectedText();
	};

	/**
	 * Sets the last focusable element that received focus. If the element is
	 * part of the PN extension (sidebar, sidebar button, topic menu, notes
	 * popup or any modal), does not set anything.
	 */
	setLastFocusedNode = () => {
		const activeElem = document.activeElement;
		if (!activeElem
			|| activeElem.closest(".aa-right-menu") || activeElem.closest(".aa-right-menu-btn")
			|| activeElem.closest(PROJECT_TOPIC_MENU_JS_SELECTOR) || activeElem.closest(COMMENT_MENU_JS_SELECTOR)
			|| activeElem.closest(".aa-ext-modal"))
			return;

		const activeTabIndex = activeElem.getAttribute("tabindex");

		if (Utils.Document.focusableNodeNames.includes(activeElem.nodeName.toUpperCase()) || (activeTabIndex && activeTabIndex > -1))
			this.lastFocusedNode = activeElem;
	};

	/**
	 * Returns an object with x,y coordinates, taken from an event object.
	 */
	getPageCoordsFromEvt = (evt: MouseEvent): pageCoords => {
		return {
			x: evt.pageX,
			y: evt.pageY
		};
	};

	handleMouseDownOnBody = (evt: MouseEvent) => {
		this.lastClickedTarget = (evt.target && evt.target instanceof Element) ? evt.target : null;
		this.lastMouseCoords = this.getPageCoordsFromEvt(evt);
		this.lastSelectionPre = Selection.GetCurrentSelection();
		this.bypassHighlightRules = evt.ctrlKey || evt.metaKey;
		if (this.websiteBehaviors)
			this.websiteBehaviors.mouseDown(evt, this.bypassHighlightRules);
	};

	handleMouseUpOnBody = async (evt: MouseEvent): void => {
		// Disable any menus on the image overlay
		const target: Element | null = (evt.target && evt.target instanceof Element) ? evt.target : null;
		if (target?.matches(".pn-shadow-host") || target?.tagName === "PN-SIDEBAR")
			return;

		// Images feature: In FF, when right clicking on an image,
		// both mouseDown and mouseUp fire here even when selecting
		// an option in the context menu. In Chrome, only mouseDown fires.
		// We ignore right clicks here because otherwise when we return
		// to the context tab with the image data, we won't have lastMouseCoords
		// data to use for menu positioning.
		if (evt.button === 2)
			return;

		const selection = Selection.GetCurrentSelection();
		const mouseCoords = this.getPageCoordsFromEvt(evt);
		const isSelectionEqual = this.renderer?.areSelectionsEqual(selection, this.lastSelectionPre);
		const isPageCoordEqual = this.renderer?.arePageCoordsEqual(mouseCoords, this.lastMouseCoords);
		const isTextSelectionEvt = !isSelectionEqual && !isPageCoordEqual;

		this.lastSelectionPre = null;
		this.lastMouseCoords = null;

		const isMenuClick = (() => {
			let isInside = !!(target?.closest(PROJECT_TOPIC_MENU_JS_SELECTOR));

			if (isInside) {
				const isPageTitle = !!(target?.closest(".js-project-name"));
				if (isPageTitle)
					isInside = false;
			}

			return isInside;
		})();

		// Check if popups need to be closed.
		// User clicked outside of topics popup
		if (this.projectTopicMenu.isMenuOpen() && !isMenuClick) {
			if (!isPageCoordEqual && !isTextSelectionEvt)
				return; // sloppy click. let them try again.

			this.projectTopicMenu.destroy();

			if (!isTextSelectionEvt) {
				Selection.ClearTextSelection();
				return;
			}
		}

		// If clicked inside any item from PROJECT_TOPIC_MENU_JS_SELECTOR (project-topic popup)
		// bypass following logic, otherwise, current selection can be
		// modified.
		if (isMenuClick)
			return;

		const processOrgProxy = (url: string, toPublicUrl = false) => UrlUtils.processOrgProxy(Data.app.me, url, toPublicUrl);

		const shouldOpenPopup = this.websiteBehaviors && await this.websiteBehaviors.mouseUp(
			evt,
			this.bypassHighlightRules,
			isSelectionEqual,
			isPageCoordEqual,
			this.page,
			processOrgProxy
		);

		if (shouldOpenPopup) {
			// Save selection here in case something happens to it before a topic is selected
			this.lastSelection = selection;
			this.lastSelectedText = Selection.GetSelectedText();

			const offset = this.calculatePositioningOffset();
			this.openProjectTopicMenuNewAnnotation(evt.pageY + offset.y, evt.pageX + offset.x, false);
		}
	};

	/**
	 * Sets up a keyboard shortcut for creating highlights, as we can't access
	 * the context menu in integrations.
	 *
	 * This shortcut only triggers when there has been text selected on the page
	 * using keyboard, and opens the project-topics menu. It will also prevent
	 * the default browser behavior of that shortcut while there is text
	 * selected using keyboard.
	 */
	initKeyboardHighlights = () => {
		hotkeys("ctrl+h", async (event) => {
			if (!this.isKeyboardSelection)
				return;

			// If there's a nested highlight, open the comments menu for it
			const selection = Selection.GetCurrentSelection();
			const nestedHighlightUuid = Selection.GetNestedHighlight(selection);

			if (nestedHighlightUuid) {
				event.preventDefault();

				const nestedToggle = getHighlightToggle(nestedHighlightUuid);
				nestedToggle.click();
				return;
			}

			// If we can create a new annotation, open the project topics menu
			const processOrgProxy = (url, toPublicUrl = false) => UrlUtils.processOrgProxy(this.store.userData.account, url, toPublicUrl);

			const shouldOpenPopup = await this.websiteBehaviors.mouseUp(
				this.lastEvent,
				true,
				false,
				false,
				this.page,
				processOrgProxy
			);

			if (shouldOpenPopup) {
				event.preventDefault();

				const offset = this.calculatePositioningOffset();
				this.openProjectTopicMenuNewAnnotation(
					this.lastMouseCoords.y + offset.y,
					this.lastMouseCoords.x + offset.x,
					true
				);

				// If the selection was lost, recover it on the page
				setTimeout(() => {
					if (window.getSelection()?.toString().length === 0)
						this.renderer.renderTempHighlight(this.websiteBehaviors, Data.app.me);
				}, 300);
			}
		});
	};

	/**
	 * Reset flag for determining whether a selection was made with keyboard.
	 */
	handleSelectionChange = () => {
		if (window.getSelection()?.toString() === "" && this.isKeyboardSelection)
			this.isKeyboardSelection = false;
	};

	/**
	 * Keeps track of selection start made with keyboard.
	 */
	handleKeydownOnBody = (event: KeyboardEvent) => {
		const {key, shiftKey} = event;

		// Shift + Arrow keys creates selections on the page in caret browsing mode
		const arrowKeys = ["ArrowDown", "ArrowLeft", "ArrowRight", "ArrowUp"];
		if (shiftKey && arrowKeys.includes(key) && !this.isSelectingKeyboard)
			this.isSelectingKeyboard = true;
	};

	/**
	 * Keeps track of selection end made with keyboard and sends a timed toast
	 * about creating keyboard highlights with PN.
	 */
	handleKeyupOnBody = (event: KeyboardEvent) => {
		const {key, shiftKey} = event;

		// Keyboard selection finished, as the shift key was released
		if (this.isSelectingKeyboard && !shiftKey) {
			this.isSelectingKeyboard = false;

			// Send a timed toast about creating a highlight with keyboard
			const timeNow = Date.now();
			const timeSinceLastToast = timeNow - this.lastKeyboardHighlightToast;
			if (!this.lastKeyboardHighlightToast
				|| (timeSinceLastToast > KEYBOARD_SELECTION_TOAST_INTERVAL)) {
				this.lastKeyboardHighlightToast = timeNow;

				const shortcut = "Ctrl + H";

				toast(`Create a highlight using the shortcut ${shortcut}`);
			}

			// Set flag that current selection was made with keyboard
			this.isKeyboardSelection = true;

			this.renderer.saveSelectionTemporarily();

			// Find the coordinates of the end of the selection
			const selectionRect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
			this.lastMouseCoords = {
				x: parseInt(selectionRect.right, 10),
				y: parseInt(selectionRect.bottom, 10)
			};
		}
	};

	openProjectTopicMenuNewAnnotation = (pageY: number, pageX: number, isKeyboard: boolean) => {
		this.projectTopicMenu.destroy();
		this.projectTopicMenu.renderMenuAt(pageY, pageX, undefined, isKeyboard, async (topicUuid: topicUuid, menuTop: number, menuLeft: number) => {
			// Callback when the topic is selected
			const stub = this.generateHighlightStub();
			if (!Data.app.project)
				throw new Error();
			const annotation = await Data.annotation.create(stub, Data.app.project.uuid, topicUuid);

			document.getSelection()?.removeAllRanges();

			// render highlight
			this.renderer.removeTmpAnnotation();
			this.renderer.renderHighlight(annotation, this.websiteBehaviors, Data.app.me, Data.topic.byUuid(topicUuid)?.color);

			// present comments menu
			await this.commentMenu.renderCommentsMenu(menuTop, menuLeft, annotation, true, true);

			// finish building the annotation object (asynchronously)
			const citation = await Citation.getCitationData(annotation.pageData);

			// persist the annotation (locally and remotely)
			await Data.annotation.update(annotation.uuid, Data.app.project.uuid, {citation});

			toast({content: "Highlight added", id: "highlight-added"});

			// enable buttons on comment menu
			this.commentMenu.newAnnotationSaved(annotation);
		});
	};

	/**
	 * Returns true if the account is allowed to see highlights, based on its current features.
	 * For example, this will return false if the user hasn't verified their email address and it's past the initial 72 hours.
	 */
	validateFeatures = (supportLevel: string) => {
		switch (supportLevel) {
			case "none": // User hasn't verified their email, and their trial has expired
			case "subNeeded":
			case "read": // User has read access after their basic trial or subscription has expired
				return false;

			case "unverified": // User hasn't verified their email, and their trial (72 hr) is still active
			case "write": // User has an active subscription
				return true;

			default:
				console.error("validateFeatures(): Invalid support level", supportLevel);
				return false;
		}
	};

	get supportLevel(): string {
		if (!Data.app.me && this.integrationPreDataMode)
			return "write";
		return Utils.Features.getSupportLevelData(Data.app.me).supportLevel;
	}

	reInit = async () => {
		// See also the destroy() method.
		if (!this.active)
			return;

		this.disableHighlighting();
		this.enableHighlighting();

		this.disableHighlightEditing();
		this.enableHighlightEditing();
		this.renderAnnotationsOnPage();
	};

	async reinitIntegration() {
		if (!Integration.isIntegration())
			throw new Error();

		this.projectTopicMenu = null;
		this.commentMenu = null;

		return this.initIntegration();
	}

	enable = async () => {
		// Our reinit methods depend on Session's "pnEnable" event handler
		await new Promise(r => setTimeout(r, 100));

		if (Integration.isIntegration())
			this.reinitIntegration();
		else
			this.reInit();
	};

	async initIntegration() {
		if (!Integration.isIntegration())
			throw new Error("Not an integration");

		window.addEventListener("pnDisable", this.destroy);
		window.addEventListener("pnEnable", this.enable);

		if (Session.startDisabled)
			return;

		if (Data.app.isLoggedIn) {
			console.log("PN Highlighter: initializing normally as there is an active session");
			return this.init();
		}

		when(() => Data.app.isLoggedIn, this.init);

		this.integrationPreDataMode = true;

		this.websiteBehaviors = this.getWebsiteBehaviors();

		// Save last focused non-PowerNotes element
		document.body.onkeydown = this.setLastFocusedNode;
		document.body.onclick = this.setLastFocusedNode;

		// Add web components/custom elements
		this.injectWebComponents();

		// Insert stylesheet ASAP so things like fonts will work
		this.injectStylesheet(PN_STYLESHEET);

		await this.websiteBehaviors.init();

		// generate rendering engine
		this.renderer = new Renderer({
			hooks: {
				onHighlightSelect: this.onHighlightSelect
			}
		});

		// page data utils
		this.page.setProcessOrgProxyFn(url => url); // Can't do proxy stuff without an account

		this.page.setWebsiteBehaviors(this.websiteBehaviors);

		// init rendering engine and event handlers for highlighting
		this.enableHighlighting();

		if (!this.projectTopicMenu) {
			this.projectTopicMenu = new ProjectTopicMenu({
				data: {
					websiteBehaviors: this.websiteBehaviors,
					context:          this
				},
				hooks: {
					copySelectedText:           this.copySelectedText,
					beforeFirstDataInteraction: this.beforeFirstDataInteraction.bind(this)
				}
			});
		}

		if (!this.commentMenu) {
			this.commentMenu = new CommentMenu({
				data: {
					context: this
				},
				hooks: {
					onCitationClick:            this.editCitationForAnnotation,
					showMessageOnSidebarButton: this.topFrameShowMessageOnSidebarButton,
					showNewAnnotationAnimation: () => {}, // TODO
					updateAnnotationTopic:      this.updateAnnotationTopic
				}
			});
		}
	}

	/**
	 * Creates a temporary account, logs into it, and selects its default project.
	 * Then, re-initializes the highlighter.
	 */
	async beforeFirstDataInteraction(): Promise<void> {
		if (!this.integrationPreDataMode)
			return;

		await Data.app.createIntegrationAccount(Integration.name, Integration.getClientToken);
		Integration.gtag("event", "init_temp_account");

		dispatchEvent(new CustomEvent("pnOpenSidebar", {bubbles: true, composed: true}));

		this.integrationPreDataMode = false;
	}

	init = async () => {
		if (this.initState !== INIT_STATE_UNINITIALIZED) {
			console.debug("PN: skipping repeat init");

			// If logged in, make sure to set this flag to false to prevent strange errors
			if (Data.app.isLoggedIn)
				this.integrationPreDataMode = false;

			return;
		}

		window.addEventListener("pnDisable", this.destroy);
		window.addEventListener("pnEnable", this.enable);

		const fromIntegrationMode: boolean = this.integrationPreDataMode;
		if (fromIntegrationMode)
			this.integrationPreDataMode = false;

		await Async.waitUntil(() => Data.app.me);

		this.websiteBehaviors = this.getWebsiteBehaviors();

		// Save last focused non-PowerNotes element
		document.body.onkeydown = this.setLastFocusedNode;
		document.body.onclick = this.setLastFocusedNode;

		// Check if we're using sticky tooltips or not
		const prefs = Account.getPrefsFromAccount(Data.app.me);
		setStickyTooltips(prefs.stickyTooltips);

		// Handles changes to location hash
		window.addEventListener("hashchange", this.handleChangeHash);

		// Add web components/custom elements
		this.injectWebComponents();

		// Insert stylesheet ASAP so things like fonts will work
		this.injectStylesheet(PN_STYLESHEET);

		await this.websiteBehaviors.init();

		// generate rendering engine
		if (!fromIntegrationMode) {
			this.renderer = new Renderer({
				hooks: {
					onHighlightSelect: this.onHighlightSelect
				}
			});
		}

		// page data utils
		this.page.setProcessOrgProxyFn((url, toPublicUrl = false) => UrlUtils.processOrgProxy(Data.app.me, url, toPublicUrl));

		this.page.setWebsiteBehaviors(this.websiteBehaviors);

		// init rendering engine and event handlers for highlighting
		this.enableHighlighting();

		if (fromIntegrationMode || !this.projectTopicMenu) {
			this.projectTopicMenu = new ProjectTopicMenu({
				data: {
					websiteBehaviors: this.websiteBehaviors,
					context:          this
				},
				hooks: {
					copySelectedText: this.copySelectedText
				}
			});
		}

		if (fromIntegrationMode || !this.commentMenu) {
			this.commentMenu = new CommentMenu({
				data: {
					context: this
				},
				hooks: {
					onCitationClick:            this.editCitationForAnnotation,
					showMessageOnSidebarButton: this.topFrameShowMessageOnSidebarButton,
					showNewAnnotationAnimation: () => {}, // TODO
					updateAnnotationTopic:      this.updateAnnotationTopic
				}
			});
		}

		// RemarQ is loaded by a script like this one.
		const hasRemarQ = (document.querySelector('script[src^="https://hermes-src.redlink.com/"]') ? true : false);
		if (hasRemarQ) {
			/* eslint-disable prefer-const */
			// eslint-disable-next-line @typescript-eslint/restrict-plus-operands
			const actualCode = "(" + function () {
				const maxCounter = 20;
				let counter = 0;
				let interval;
				const disableRemarq = function () {
					if (++counter >= maxCounter) {
						clearInterval(interval);
						return;
					}

					if (window.Remarq === undefined)
						return;

					if (counter < maxCounter - 3) // call "Remarq.stop()" 3 times
						counter = maxCounter - 3;

					window.Remarq.stop();
				};
				interval = setInterval(disableRemarq, 500);
			} + ")();";
			/* eslint-enable prefer-const */
			const script = document.createElement("script");
			script.textContent = actualCode;
			(document.head || document.documentElement).appendChild(script);
		}

		// Set data attribute on body based on page host.
		document.body.setAttribute("data-aa-page-host", window.location.host);

		runInAction(() => {
			this.active = true;
		});

		setTimeout(this.renderAnnotationsOnPage, this.websiteBehaviors.initialRenderDelay());
		this.initState = INIT_STATE_INIT_COMPLETE;

		if (typeof PDFViewerApplication !== "undefined") {
			document.querySelector("body").classList.add("aa-pdf");

			let textLayerRendered = false;
			const markTextLayerRendered = () => {
				textLayerRendered = true;
				PDFViewerApplication.eventBus.off("textlayerrendered", markTextLayerRendered); // Only do this once
			};
			setTimeout(async () => {
				// This works because the event gets triggered even if the rendered page has no text
				PDFViewerApplication.eventBus.on("textlayerrendered", markTextLayerRendered);
				try {
					await Utils.Async.waitUntil(() => textLayerRendered, 300, 10000);
				} catch (e) {} // pdfjs' textlayerrendered event never triggered
			}, 500);
		}
	};

	get timeTrackingTermsPendingReview(): boolean {
		if (!Data.app.me || !Data.app.project)
			return false;
		return Project.timeTrackingTermsPendingReview(Data.app.me, Data.app.project);
	};

	getWebsiteBehaviors(): WebsiteBehaviors {
		const isPdf = window.PDFViewerApplication !== undefined;
		let url = window.location.href;

		if (isPdf) {
			const pathname = window.location.pathname;
			if (pathname !== undefined && /^\/(http|https|file|ftp):\/\//.test(pathname)) {
				// Remove the first slash char (leaves a proper URL due to how the PDF viewer works in our ext)
				url = window.location.pathname.substr(1);
			}
		}

		if (Data.app.me)
			url = UrlUtils.processOrgProxy(Data.app.me, url, true);

		const websiteBehaviors = getWebsiteBehaviors(url, isPdf, {
			destroy:           () => {this.destroy();},
			injectStyles:      () => {this.injectStylesheet(PN_STYLESHEET);},
			renderAnnotations: () => {this.renderAnnotationsOnPage();},
			reInit:            () => {this.reInit();},
			minimizeSidebar:   () => {} // TODO
		});
		websiteBehaviors?.setDefaultInitialRenderDelayMs(100); // Our rendering logic is better now so we can set this low

		console.debug("PN: Website behaviors", {
			identifier: websiteBehaviors.identifier(),
			url
		});

		// Because we're not in the context of an extension, we can run this immediately.
		// See "isolated" vs. "main" world: https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/manifest.json/content_scripts
		websiteBehaviors.initMainWorld();

		return websiteBehaviors;
	}

	/**
	 * Enable all highlighting support,
	 * including both rendering and creating.
	 */
	enableHighlighting = () => {
		if (this.timeTrackingTermsPendingReview || this.supportLevel === "none" || this.supportLevel === "read" || this.supportLevel === "subNeeded") {
			// Don't render highlights if the user needs to review the time-tracking terms,
			// or if their basic trial has expired.
			if (this.isHighlightingEnabled)
				this.disableHighlighting();
			return;
		}

		if (this.isHighlightingEnabled)
			return;

		this.websiteBehaviors.init();

		// Enable event handlers for creating highlights
		this.isHighlightingEnabled = true;
		this.enableHighlightEditing();
	};

	/**
	 * Disable all highlighting support,
	 * including both rendering and creating.
	 */
	disableHighlighting = () => {
		if (!this.isHighlightingEnabled)
			return;

		this.projectTopicMenu.destroy();
		this.commentMenu.destroy();
		this.websiteBehaviors.destroy();

		// Disable event handlers for creating highlights
		this.disableHighlightEditing();

		this.isHighlightingEnabled = false;
	};

	/**
	 * Enable event handlers for creating highlights.
	 */
	enableHighlightEditing = () => {
		if (!this.isHighlightingEnabled)
			return; // Don't enable event handlers if highlighting is disabled in general.

		document.body.addEventListener("mousedown", this.handleMouseDownOnBody);

		// Mouse up on body, tries to catch a selection.
		document.body.addEventListener("mouseup", this.handleMouseUpOnBody);

		// Right-click on body
		document.body.addEventListener("contextmenu", this.handleOnContextMenu);

		// Keyboard highlighting
		document.addEventListener("selectionchange", this.handleSelectionChange);
		document.body.addEventListener("keydown", this.handleKeydownOnBody);
		document.body.addEventListener("keyup", this.handleKeyupOnBody);

		this.isHighlightEditingEnabled = true;
	};

	/**
	 * Disable event handlers for creating highlights.
	 * If this is called, the user can no longer create highlights.
	 * However, if disableHighlighting() has not also been called,
	 * existing highlights can still be rendered.
	 * This is used when the user doesn't have edit access on the active project.
	 */
	disableHighlightEditing = () => {
		document.body.removeEventListener("mousedown", this.handleMouseDownOnBody);
		document.body.removeEventListener("mouseup", this.handleMouseUpOnBody);
		document.body.removeEventListener("contextmenu", this.handleOnContextMenu);

		// Keyboard highlighting
		document.removeEventListener("selectionchange", this.handleSelectionChange);
		document.body.removeEventListener("keydown", this.handleKeydownOnBody);
		document.body.removeEventListener("keyup", this.handleKeyupOnBody);

		this.isHighlightEditingEnabled = false;
	};

	/**
	 * Inject a stylesheet into the head section of the current page.
	 *
	 * @param  {string}  path  Path relative to extension files, to a css file.
	 */
	injectStylesheet = (path) => {
		const href = window?.LEO?.integrationCssUrls[path] ?? `/assets/css/temp-browser-ext/${path}`; // TODO

		const stylesheet = document.createElement("link");
		stylesheet.setAttribute("rel", "stylesheet");
		stylesheet.setAttribute("type", "text/css");
		stylesheet.setAttribute("href", href);
		stylesheet.setAttribute("id", "pn-contentscript-styles"); // see special-cases in pn-highlight-lib

		document.head.appendChild(stylesheet);
	};

	/**
	 * Removes an injected stylesheet from the head section of the current page.
	 *
	 * @param  {string}  path  Path relative to extension files, to a css file.
	 */
	removeInjectedStylesheet = (path) => {
		const href = window?.LEO?.integrationCssUrls[path] ?? `/assets/css/temp-browser-ext/${path}`; // TODO

		const stylesheet = document.querySelector('head > link[href="' + href + '"]');
		if (stylesheet)
			stylesheet.remove();
	};

	injectWebComponents = () => {
		return; // TODO: this is breaking the emoji picker
		const components = document.createElement("script");
		components.src = PN_WEB_COMPONENTS_PATH;
		components.type = "module";
		document.head.append(components);
	};

	removeInjectedWebComponents = () => {
		return;
		const path = PN_WEB_COMPONENTS_PATH;
		const components = document.querySelector(`head > script[href="${path}"]`);
		if (components)
			components.remove();
	};

	/**
	 * Remove all functionality and UI elements from page.
	 */
	destroy = () => {
		if (!this.active)
			return;

		this.disableHighlighting();

		// General
		document.body.removeAttribute("data-aa-page-host");
		document.body.removeEventListener("hashchange", this.handleChangeHash);

		this.websiteBehaviors.destroy();

		setTimeout(() => {
			this.removeInjectedStylesheet(PN_STYLESHEET);
			this.removeInjectedWebComponents();
		}, 500);

		Data.app.logout();
		Data.app._clearAllData();

		this.active = false;

		this.initState = INIT_STATE_UNINITIALIZED;
	};

	/**
	 * Create the annotation locally and on the server
	 *
	 * @param {string} topicUuid
	 * @param {Object} targetImg (optional) for images selected via context menu
	 */
	createImageAnnotation = async (topicUuid: topicUuid, targetImg?: Element) => {
		if (!Data.app.project)
			throw new Error();

		const extra = {};
		if (targetImg)
			extra.selection = {imageTreeStr: Selection.GetImageTreeStr(targetImg)};
		extra.citation = await Citation.getCitationData();

		const stub = this.generateImageStub(extra);
		return await Data.annotation.create(stub, Data.app.project.uuid, topicUuid);
	};

	/**
	 * Dispatches a custom event to open the citation panel in the sidebar.
	 */
	editCitationForAnnotation = (annotationUuid: annotationUuid) => {
		const myEvent = new CustomEvent<{annotationUuid: annotationUuid}>("pnClickCitationButton", {
			detail:   {annotationUuid},
			bubbles:  true,
			composed: true
		});

		dispatchEvent(myEvent);
	};

	get hasEditAccess(): boolean {
		if (!Data.app.me || !Data.app.project)
			return false;
		const editableSupportLevel = this.supportLevel === "write" || this.supportLevel === "unverified";
		const hasProjectLevelAccess = Project.hasAccess(Data.app.me, Data.app.project, Project.ACCESS_EDIT);
		return editableSupportLevel && hasProjectLevelAccess;
	};

	/**
	 * For an element that has position: absolute and is a child of the body
	 * element, calculates the offset needed for the element to be positioned
	 * relative to the document (html element).
	 */
	calculatePositioningOffset = (): pageCoords => {
		// If body position is static, nothing needs to be shifted
		const bodyPosition = window.getComputedStyle(document.body).position;
		if (bodyPosition === "static")
			return {x: 0, y: 0};

		const rect = document.body.getBoundingClientRect();

		return {
			x: -(Math.round(rect.left) + window.scrollX),
			y: -(Math.round(rect.top) + window.scrollY)
		};
	};
}

export default new Highlighter();
